import React, { useEffect, useRef, useState } from 'react';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Button } from 'primereact/button';
import { InputText } from 'primereact/inputtext';
import { Toast } from 'primereact/toast';

import 'primereact/resources/primereact.min.css';
import 'primeicons/primeicons.css';
import 'primeflex/primeflex.css';

import { useDispatch } from 'react-redux';
import { useFormik } from 'formik';

import { handlePostRequest } from '../../service/PostTemplate';
import { endPoints } from '../../Config';

const TransactionDetails = () => {

    const dispatch = useDispatch();
    const toast = useRef(null);

    const [tableData, setTableData] = useState([]);
    const [loadingBtn, setLoadingBtn] = useState(false);

    // DATE LIMITS
    const currentDate = new Date();
    const previousDate = currentDate.toISOString().split('T')[0];

    const minDate = new Date();
    minDate.setMonth(minDate.getMonth() - 2);

    const formattedMinDate = minDate.toISOString().split('T')[0];

    // API FUNCTION
    const fetchTransactions = async (payload) => {

    try {

        setLoadingBtn(true);

        const response = await dispatch(
            handlePostRequest(
                payload,
                endPoints.searchMerchantLiveReports,
                true
            )
        );

        console.log('API RESPONSE:', response);

        // IMPORTANT
        const apiResponse = response?.data || response;

        // ERROR RESPONSE
        if (apiResponse?.success === false) {

            toast.current.show({
                severity: 'error',
                summary: 'Error',
                detail: apiResponse?.message || 'Something went wrong',
                life: 3000
            });

            setTableData([]);

            return;
        }

        // SUCCESS RESPONSE
        if (apiResponse?.success === true) {

            const apiData = apiResponse?.data || [];

            console.log('TABLE DATA:', apiData);

            setTableData(
                Array.isArray(apiData)
                    ? apiData
                    : []
            );

            toast.current.show({
                severity: 'success',
                summary: 'Success',
                detail: apiResponse?.message || 'Data loaded successfully',
                life: 2000
            });
        }

    } catch (error) {

        console.log(error);

        toast.current.show({
            severity: 'error',
            summary: 'Error',
            detail: 'Something went wrong',
            life: 3000
        });

        setTableData([]);

    } finally {

        setLoadingBtn(false);
    }
};

    // FORMIK
    const formik = useFormik({

        initialValues: {
            fromDate: '',
            toDate: '',
            transaction_Currency: '',
            masked_Pan: '',
            issuer_Name: '',
            basket_Id: '',
            paymentName: 0,
            merchantId: 0
        },

        onSubmit: async (data) => {

            const payload = {
                fromDate: data.fromDate || null,
                toDate: data.toDate || null,
                transaction_Currency: data.transaction_Currency || null,
                masked_Pan: data.masked_Pan || null,
                issuer_Name: data.issuer_Name || null,
                basket_Id: data.basket_Id || null,
                paymentName: data.paymentName || null,
                merchantId: data.merchantId || null
            };

            console.log('PAYLOAD:', payload);

            fetchTransactions(payload);
        }
    });

    // PAGE LOAD API
    useEffect(() => {

        const payload = {
            fromDate: null,
            toDate: null,
            transaction_Currency: null,
            masked_Pan: null,
            issuer_Name: null,
            basket_Id: null,
            paymentName: null,
            merchantId: null
        };

        fetchTransactions(payload);

    }, []);

    // RESET
    const resetHandle = () => {

        formik.resetForm();

        setTableData([]);
    };

    return (
        <div className="p-4">

            {/* TOAST */}
            <Toast ref={toast} />

            {/* FILTER SECTION */}
            <div className="card Card__Round">

                <form
                    onSubmit={formik.handleSubmit}
                    className="p-fluid p-mt-2"
                >

                    <div className="p-fluid p-formgrid grid mb-5">

                        {/* FROM DATE */}
                        <div className="p-field col-12 md:col-4">

                            <label>From Date</label>

                            <InputText
                                type="date"
                                name="fromDate"
                                min={formattedMinDate}
                                max={previousDate}
                                value={formik.values.fromDate}
                                onChange={formik.handleChange}
                            />
                        </div>

                        {/* TO DATE */}
                        <div className="p-field col-12 md:col-4">

                            <label>To Date</label>

                            <InputText
                                type="date"
                                name="toDate"
                                min={formattedMinDate}
                                max={previousDate}
                                value={formik.values.toDate}
                                onChange={formik.handleChange}
                            />
                        </div>

                        {/* TRANSACTION CURRENCY */}
                        <div className="p-field col-12 md:col-4">

                            <label>Transaction Currency</label>

                            <InputText
                                name="transaction_Currency"
                                placeholder="Currency"
                                value={formik.values.transaction_Currency}
                                onChange={formik.handleChange}
                            />
                        </div>

                        {/* MASKED PAN */}
                        <div className="p-field col-12 md:col-4">

                            <label>Masked PAN</label>

                            <InputText
                                name="masked_Pan"
                                placeholder="Masked PAN"
                                value={formik.values.masked_Pan}
                                onChange={formik.handleChange}
                            />
                        </div>

                        {/* ISSUER NAME */}
                        <div className="p-field col-12 md:col-4">

                            <label>Issuer Name</label>

                            <InputText
                                name="issuer_Name"
                                placeholder="Issuer Name"
                                value={formik.values.issuer_Name}
                                onChange={formik.handleChange}
                            />
                        </div>

                        {/* BASKET ID */}
                        <div className="p-field col-12 md:col-4">

                            <label>Basket ID</label>

                            <InputText
                                name="basket_Id"
                                placeholder="Basket ID"
                                value={formik.values.basket_Id}
                                onChange={formik.handleChange}
                            />
                        </div>

                        {/* BUTTONS */}
                        <div className="p-field col-12 flex gap-2 mt-3">

                            {/* SEARCH */}
                            <Button
                                type="submit"
                                label="Search"
                                icon="pi pi-search"
                                className="Btn__Dark__Search"
                                loading={loadingBtn}
                            />

                            {/* RESET */}
                            <Button
                                type="button"
                                label="Reset"
                                onClick={resetHandle}
                                className="Btn__Transparent__Reset"
                            />
                        </div>
                    </div>
                </form>
            </div>

            {/* TABLE HEADER */}
            <div className="mt-4 mb-3">

                <h2 className="text-2xl font-semibold">
                    Settled Transaction Report
                </h2>
            </div>

            {/* TABLE */}
            <div className="card">

                <DataTable
    value={tableData}
    paginator
    rows={15}
    rowsPerPageOptions={[5, 10, 20]}
    emptyMessage="No records found"
    responsiveLayout="scroll"
    className="p-datatable-sm"
>

    <Column
        field="basket_Id"
        header="Basket ID"
    />

    <Column
        field="order_Date"
        header="Order Date"
    />

    <Column
        field="paymentName"
        header="Payment Name"
    />

    <Column
        field="amount"
        header="Amount"
    />

    <Column
        field="transaction_Currency"
        header="Currency"
    />

    <Column
        field="issuer_Name"
        header="Issuer Name"
    />

    <Column
        field="masked_Pan"
        header="Masked PAN"
    />

</DataTable>
            </div>
        </div>
    );
};

export default TransactionDetails;